import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeRemoteCityAdapterConfig } from "../../../usecases/city-adapter-config/remote-city-adapter-config-factory";
import { makeRemoteServiceTaxationCodeAdmin } from "../../../usecases/service-taxation-code/remote-service-taxation-code-admin-factory";
import {
  AdapterConfig,
  AdapterPreviewModel,
  AdapterSimulateModel,
  CityAdapterConfigModel,
  EMPTY_CONFIG,
} from "../../../../../domain/models/city-adapter-config";
import { CityRuleCountModel } from "../../../../../domain/models/service-taxation-code";
import CityAdapterConfigPage from "../../../../../presentation/pages/admin/city-adapter-config";
import toastUtils from "../../../../../utils/ToastUtils";

const MakeCityAdapterConfig: React.FC = () => {
  const adapterService = useMemo(() => makeRemoteCityAdapterConfig(), []);
  const cityService = useMemo(() => makeRemoteServiceTaxationCodeAdmin(), []);

  // Busca de cidades
  const [cities, setCities] = useState<CityRuleCountModel[]>([]);
  const [citySearch, setCitySearch] = useState("");
  const [cityLoading, setCityLoading] = useState(false);
  const [cityTotalPages, setCityTotalPages] = useState(0);
  const [citiesWithConfig, setCitiesWithConfig] = useState<string[]>([]);

  // Cidade selecionada
  const [selectedCity, setSelectedCity] = useState<CityRuleCountModel | null>(null);
  const [selectedLayout, setSelectedLayout] = useState<string>("NATIONAL");

  // Config do adapter
  const [existingConfig, setExistingConfig] = useState<CityAdapterConfigModel | null>(null);
  const [createMode, setCreateMode] = useState(false);
  const [editMode, setEditMode] = useState<"form" | "json">("form");
  const [editConfig, setEditConfig] = useState<AdapterConfig>(EMPTY_CONFIG);
  const [editAtivo, setEditAtivo] = useState(true);
  const [saving, setSaving] = useState(false);

  // Preview
  const [preview, setPreview] = useState<AdapterPreviewModel | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Simulação
  const [simulation, setSimulation] = useState<AdapterSimulateModel | null>(null);
  const [simulationLoading, setSimulationLoading] = useState(false);

  // --- Carregar lista de cidades com config (para badge) ---
  const loadConfiguredCities = useCallback(async (): Promise<void> => {
    try {
      const configured = await adapterService.listConfiguredCities();
      setCitiesWithConfig(configured);
    } catch {
      // silently fail
    }
  }, [adapterService]);

  // --- Busca de cidades ---
  const handleSearchCities = useCallback(async (search: string, page = 0): Promise<void> => {
    try {
      setCityLoading(true);
      const sanitized = search.replace(/-/g, "");
      const response = await cityService.searchCities(sanitized, page, 20);
      setCities(response.content);
      setCityTotalPages(response.totalPages);
    } catch {
      toastUtils.showMessage({ type: "error", message: "Erro ao buscar cidades." });
    } finally {
      setCityLoading(false);
    }
  }, [cityService]);

  useEffect(() => {
    handleSearchCities(citySearch);
    loadConfiguredCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCitySearchChange = useCallback((search: string): void => {
    setCitySearch(search);
    handleSearchCities(search, 0);
  }, [handleSearchCities]);

  const onCityPageChange = useCallback((page: number): void => {
    handleSearchCities(citySearch, page);
  }, [handleSearchCities, citySearch]);

  // --- Selecionar cidade ---
  const handleSelectCity = useCallback(async (city: CityRuleCountModel, layout = "NATIONAL"): Promise<void> => {
    setSelectedCity(city);
    setSelectedLayout(layout);
    setSimulation(null);

    try {
      setCityLoading(true);
      const config = await adapterService.getByIbge(city.ibge, layout);
      setExistingConfig(config);
      setEditConfig(config.config);
      setEditAtivo(config.ativo);
      setCreateMode(false);
    } catch {
      setExistingConfig(null);
      setEditConfig(EMPTY_CONFIG);
      setEditAtivo(true);
      setCreateMode(true);
    } finally {
      setCityLoading(false);
    }

    // Carrega preview
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const prev = await adapterService.previewPayload(city.ibge, layout);
      setPreview(prev);
    } catch (e: any) {
      setPreview(null);
      setPreviewError(e?.message || "Sem pré-visualização disponível.");
    } finally {
      setPreviewLoading(false);
    }
  }, [adapterService]);

  // --- Voltar ---
  const handleBack = useCallback((): void => {
    setSelectedCity(null);
    setSelectedLayout("NATIONAL");
    setExistingConfig(null);
    setCreateMode(false);
    setPreview(null);
    setSimulation(null);
    loadConfiguredCities();
  }, [loadConfiguredCities]);

  // --- Salvar ---
  const handleSave = useCallback(async (): Promise<void> => {
    if (!selectedCity) return;
    try {
      setSaving(true);
      if (createMode) {
        const cityName = `${selectedCity.name}${selectedCity.uf ? `-${selectedCity.uf}` : ""}`;
        await adapterService.create({
          cityIbge: selectedCity.ibge,
          nomeCidade: cityName,
          layout: selectedLayout,
          config: editConfig,
          ativo: editAtivo,
        });
        toastUtils.showMessage({ type: "success", message: "Configuração criada com sucesso!" });
      } else {
        await adapterService.update(selectedCity.ibge, selectedLayout, {
          config: editConfig,
          ativo: editAtivo,
        });
        toastUtils.showMessage({ type: "success", message: "Configuração salva com sucesso!" });
      }
      handleBack();
    } catch {
      toastUtils.showMessage({ type: "error", message: "Erro ao salvar configuração." });
    } finally {
      setSaving(false);
    }
  }, [adapterService, selectedCity, selectedLayout, createMode, editConfig, editAtivo, handleBack]);

  // --- Deletar ---
  const handleDelete = useCallback(async (): Promise<void> => {
    if (!selectedCity) return;
    try {
      await adapterService.remove(selectedCity.ibge, selectedLayout);
      toastUtils.showMessage({ type: "success", message: "Configuração excluída!" });
      handleBack();
    } catch {
      toastUtils.showMessage({ type: "error", message: "Erro ao excluir." });
    }
  }, [adapterService, selectedCity, selectedLayout, handleBack]);

  // --- Simular ---
  const handleSimulate = useCallback(async (): Promise<void> => {
    if (!selectedCity) return;
    try {
      setSimulationLoading(true);
      const result = await adapterService.simulate(selectedCity.ibge, selectedLayout, editConfig);
      setSimulation(result);
    } catch (e: any) {
      toastUtils.showMessage({ type: "error", message: e?.message || "Erro na simulação." });
    } finally {
      setSimulationLoading(false);
    }
  }, [adapterService, selectedCity, selectedLayout, editConfig]);

  // --- Trocar layout ---
  const handleLayoutChange = useCallback(async (layout: string): Promise<void> => {
    if (!selectedCity) return;
    setSelectedLayout(layout);
    setSimulation(null);

    try {
      setCityLoading(true);
      const config = await adapterService.getByIbge(selectedCity.ibge, layout);
      setExistingConfig(config);
      setEditConfig(config.config);
      setEditAtivo(config.ativo);
      setCreateMode(false);
    } catch {
      setExistingConfig(null);
      setEditConfig(EMPTY_CONFIG);
      setEditAtivo(true);
      setCreateMode(true);
    } finally {
      setCityLoading(false);
    }

    // Carrega preview do novo layout
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const prev = await adapterService.previewPayload(selectedCity.ibge, layout);
      setPreview(prev);
    } catch (e: any) {
      setPreview(null);
      setPreviewError(e?.message || "Sem pré-visualização disponível.");
    } finally {
      setPreviewLoading(false);
    }
  }, [adapterService, selectedCity]);

  return (
    <CityAdapterConfigPage
      // Busca de cidades
      cities={cities}
      citySearch={citySearch}
      cityLoading={cityLoading}
      cityTotalPages={cityTotalPages}
      onCitySearchChange={onCitySearchChange}
      onCityPageChange={onCityPageChange}
      onSelectCity={handleSelectCity}
      citiesWithConfig={citiesWithConfig}
      // Cidade selecionada
      selectedCity={selectedCity}
      selectedLayout={selectedLayout}
      onLayoutChange={handleLayoutChange}
      existingConfig={existingConfig}
      createMode={createMode}
      // Edição
      editMode={editMode}
      onEditModeChange={setEditMode}
      editConfig={editConfig}
      onEditConfigChange={setEditConfig}
      editAtivo={editAtivo}
      onEditAtivoChange={setEditAtivo}
      onSave={handleSave}
      onDelete={existingConfig ? handleDelete : undefined}
      onBack={handleBack}
      saving={saving}
      // Preview
      preview={preview}
      previewLoading={previewLoading}
      previewError={previewError}
      // Simulação
      simulation={simulation}
      simulationLoading={simulationLoading}
      onSimulate={handleSimulate}
    />
  );
};

export default MakeCityAdapterConfig;
